import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Box, Typography, Paper, Stack, Chip, useTheme, Tab, Tabs,
  TextField, InputAdornment, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip, Avatar, alpha,
  Grid, Divider, Slide
} from "@mui/material";
import {
  SearchRounded as SearchIcon,
  LocalActivityOutlined as BookingIcon,
  CancelOutlined as CancelIcon,
  VisibilityOutlined as ViewIcon,
  FilterList as FilterIcon,
  AccountCircle as UserIcon
} from "@mui/icons-material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { getPartnerBookings, cancelBookingByPartner } from "../../store/slices/bookingSlice";
import { toast } from "react-toastify";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PartnerBookings = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { user: clerkUser } = useUser();
  const { bookings, pagination, loading } = useSelector(s => s.booking);
  const isDark = mode === 'dark';

  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: isDark ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: isDark ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: isDark ? "#282A42" : "#ffffff",
    paper: isDark ? "#30334E" : "#ffffff",
    divider: isDark ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [isDark]);

  const [page, setPage] = useState(0);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [detailDialog, setDetailDialog] = useState(null);

  useEffect(() => {
    if (clerkUser?.id) {
      const theaterId = localStorage.getItem('activeTheaterId');
      const status = tab === 0 ? 'all' : tab === 1 ? 'confirmed' : tab === 2 ? 'pending' : 'cancelled';
      dispatch(getPartnerBookings({ userId: clerkUser.id, params: { page: page + 1, limit: 100, status, theaterId } }));
    }
  }, [dispatch, clerkUser?.id, tab]);

  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      const theaterId = localStorage.getItem('activeTheaterId');
      await dispatch(cancelBookingByPartner({
        userId: clerkUser.id, bookingId: cancelDialog._id, reason: cancelReason, theaterId
      })).unwrap();
      toast.success("Booking cancelled & seats released");
      setCancelDialog(null); setCancelReason('');
    } catch (err) { toast.error(err.message || "Failed to cancel"); }
  };

  const statusChip = (status) => {
    const c = {
      confirmed: { color: colors.success, label: 'Confirmed' },
      pending: { color: colors.warning, label: 'Pending' },
      cancelled: { color: colors.error, label: 'Cancelled' }
    };
    const cfg = c[status] || c.pending;
    return <Chip label={cfg.label} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 900, bgcolor: alpha(cfg.color, 0.12), color: cfg.color }} />;
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'bookingId',
      header: 'Node ID',
      Cell: ({ renderedCellValue }) => (
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: colors.primary }}>{renderedCellValue}</Typography>
      ),
    },
    {
      accessorKey: 'movieTitle',
      header: 'Production Title',
      Cell: ({ row }) => (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{row.original.movieTitle || row.original.show?.movie?.title || 'N/A'}</Typography>
      ),
    },
    {
      accessorKey: 'showTime',
      header: 'Temporal Slot',
      Cell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{row.original.showTime}</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary }}>{row.original.showDate ? new Date(row.original.showDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'N/A'}</Typography>
        </Box>
      ),
    },
    {
      accessorKey: 'seats',
      header: 'Load Units',
      Cell: ({ renderedCellValue }) => (
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 800 }}>{renderedCellValue?.join(', ')}</Typography>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'GTV',
      Cell: ({ renderedCellValue }) => (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: colors.success }}>₹{renderedCellValue}</Typography>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Protocol State',
      Cell: ({ renderedCellValue }) => statusChip(renderedCellValue),
    },
    {
      id: 'actions',
      header: 'Operations',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Deep Inspect">
            <IconButton size="small" onClick={() => setDetailDialog(row.original)} sx={{ color: colors.primary, bgcolor: alpha(colors.primary, 0.08) }}>
              <ViewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {row.original.status === 'confirmed' && (
            <Tooltip title="Revoke Protocol">
              <IconButton size="small" onClick={() => setCancelDialog(row.original)} sx={{ color: colors.error, bgcolor: alpha(colors.error, 0.08) }}>
                <CancelIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ], [colors]);

  const table = useMaterialReactTable({
    columns,
    data: bookings,
    enableColumnActions: false,
    enableGlobalFilter: true,
    enablePagination: true,
    enableSorting: true,
    initialState: { showGlobalFilter: true },
    muiSearchTextFieldProps: {
      placeholder: 'Search ledger...',
      sx: { minWidth: '300px' },
      variant: 'outlined',
      size: 'small',
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '12px',
        border: `1px solid ${colors.divider}`,
        bgcolor: colors.paper,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 800,
        fontSize: '0.7rem',
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        py: 2,
        bgcolor: alpha(colors.textPrimary, 0.02),
      },
    },
  });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5 }}>
            Booking Intelligence
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            Audit and verify incoming transactions across your theater network
          </Typography>
        </Box>
        <Chip icon={<FilterIcon sx={{ fontSize: '14px !important' }} />} label="AUTO-SYNC ACTIVE" size="small" sx={{ height: 28, bgcolor: alpha(colors.info, 0.1), color: colors.info, fontWeight: 800, fontSize: '0.65rem' }} />
      </Box>      <Paper elevation={0} sx={{ p: 0.5, mb: 3, bgcolor: alpha(colors.textPrimary, 0.05), border: 'none', borderRadius: '12px', width: 'fit-content' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, fontSize: '0.75rem', minHeight: 40, color: colors.textSecondary, borderRadius: '8px', transition: '0.2s', mx: 0.5 },
            '& .Mui-selected': { bgcolor: colors.background, color: `${colors.primary} !important`, boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.06)' },
            '& .MuiTabs-indicator': { display: 'none' }
          }}
        >
          <Tab label="Registry Matrix" /><Tab label="Confirmed" /><Tab label="Pending" /><Tab label="Cancelled" />
        </Tabs>
      </Paper>

      <MaterialReactTable table={table} />

      {/* Revoke Dialog */}
      <Dialog
        open={!!cancelDialog}
        onClose={() => setCancelDialog(null)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: isDark ? alpha('#0f1014', 0.95) : '#fff',
            backgroundImage: 'none',
            border: `1px solid ${alpha(colors.error, 0.2)}`,
            backdropFilter: 'blur(30px)',
            boxShadow: isDark ? '0 50px 100px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 2 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error, width: 56, height: 56, borderRadius: '18px', border: `1px solid ${alpha(colors.error, 0.2)}` }}>
              <CancelIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: '1.5rem', color: colors.textPrimary, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                Revoke Authority
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.error, letterSpacing: '0.12em', textTransform: 'uppercase', mt: 0.5 }}>
                Operational Override Required
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 4, pt: 1 }}>
          <Typography sx={{ color: colors.textSecondary, mb: 3, fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.6 }}>
            Confirm permanent revocation for reservation <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 900 }}>{cancelDialog?.bookingId}</Box>. This will trigger an automated financial roll-back of <Box component="span" sx={{ color: colors.success, fontWeight: 900 }}>₹{cancelDialog?.totalAmount}</Box>.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Specify reason for protocol revocation..."
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: alpha(colors.textPrimary, 0.02),
                '& fieldset': { borderColor: alpha(colors.textPrimary, 0.1) },
              },
              '& .MuiInputLabel-root': { fontWeight: 700, fontSize: '0.85rem' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 1, justifyContent: 'space-between' }}>
          <Button
            onClick={() => setCancelDialog(null)}
            sx={{ fontWeight: 800, color: colors.textSecondary, px: 3, borderRadius: '12px' }}
          >
            Abort Termination
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            sx={{
              borderRadius: '16px',
              px: 5,
              py: 2,
              fontWeight: 900,
              boxShadow: `0 15px 30px ${alpha(colors.error, 0.3)}`,
              '&:hover': { transform: 'translateY(-2px)' }
            }}
          >
            Execute Revocation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={!!detailDialog}
        onClose={() => setDetailDialog(null)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '28px',
            bgcolor: isDark ? alpha('#0f1014', 0.95) : '#fff',
            backgroundImage: 'none',
            border: `1px solid ${alpha(colors.textPrimary, 0.08)}`,
            backdropFilter: 'blur(30px)',
            boxShadow: isDark ? '0 50px 100px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.08)'
          }
        }}
      >
        <DialogTitle sx={{ p: 4, pb: 2 }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Avatar sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary, width: 56, height: 56, borderRadius: '18px', border: `1px solid ${alpha(colors.primary, 0.2)}` }}>
              <BookingIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: '1.5rem', color: colors.textPrimary, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                Node Telemetry
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.primary, letterSpacing: '0.12em', textTransform: 'uppercase', mt: 0.5 }}>
                Transaction Lifecycle Audit
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 4, pt: 1 }}>
          {detailDialog && (
            <Stack spacing={3}>
              <Box sx={{ p: 2.5, borderRadius: '20px', bgcolor: alpha(colors.primary, 0.05), border: `1px solid ${alpha(colors.primary, 0.1)}` }}>
                <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>Transaction Identifier</Typography>
                <Typography sx={{ fontWeight: 950, fontSize: '1.4rem', color: colors.primary, letterSpacing: '0.05em' }}>{detailDialog.bookingId}</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 800, textTransform: 'uppercase' }}>Production Title</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', mt: 0.5 }}>{detailDialog.movieTitle || detailDialog.show?.movie?.title}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 800, textTransform: 'uppercase' }}>Theater Hub</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', mt: 0.5 }}>{detailDialog.theaterName}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 800, textTransform: 'uppercase' }}>Temporal Matrix</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mt: 0.5 }}>{detailDialog.showDate ? new Date(detailDialog.showDate).toLocaleDateString() : 'N/A'} @ {detailDialog.showTime}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 800, textTransform: 'uppercase' }}>Capacity Load</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mt: 0.5, color: colors.primary }}>{detailDialog.seats?.join(', ')}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ borderStyle: 'dashed', opacity: 0.1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, borderRadius: '24px', bgcolor: alpha(colors.success, 0.08), border: `1px solid ${alpha(colors.success, 0.15)}` }}>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: colors.success, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total GTV Contribution</Typography>
                  <Typography sx={{ fontWeight: 950, fontSize: '2rem', color: colors.success }}>₹{detailDialog.totalAmount}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 900, textTransform: 'uppercase', mb: 1, letterSpacing: '0.05em' }}>Network State</Typography>
                  {statusChip(detailDialog.status)}
                </Box>
              </Box>

              {detailDialog.userDetails?.email && (
                <Box sx={{ p: 2, borderRadius: '16px', bgcolor: alpha(colors.textPrimary, 0.03) }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(colors.textSecondary, 0.1) }}>
                      <UserIcon sx={{ fontSize: 20, color: colors.textSecondary }} />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 900, textTransform: 'uppercase' }}>Origin Identity</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{detailDialog.userDetails.email}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 1 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setDetailDialog(null)}
            sx={{
              borderRadius: '16px',
              py: 2,
              fontWeight: 950,
              bgcolor: alpha(colors.textPrimary, 0.1),
              color: colors.textPrimary,
              '&:hover': { bgcolor: alpha(colors.textPrimary, 0.2) }
            }}
          >
            Dismiss Operational Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerBookings;
